import { BehaviorSubject } from 'rxjs'
import { message } from 'ant-design-vue'
import { cloneDeep } from 'lodash'
import {
  ShiftSimpleVO,
  ShiftVO,
  ShiftServiceProxy,
  ShiftUpdateBasicDTO,
} from '@/service-proxies/app-v2/time.service-proxies'
import i18n from '@/locales'
import { Action, Loading, Visible } from './types'

const ssp = new ShiftServiceProxy()

export const state = {
  modelList: new BehaviorSubject<ShiftSimpleVO[]>([]),
  currentModelId: new BehaviorSubject(''),
  currentModelDetail: new BehaviorSubject<ShiftVO>(null),
  loading: new BehaviorSubject<Loading>({
    modelTable: false,
    modelDetail: false,
    createForm: false,
    updateForm: false,
  }),
  visible: new BehaviorSubject<Visible>({ createForm: false, updateForm: false }),
}

export const types = {
  SET_MODEL_LIST: 'SET_MODEL_LIST',
  SET_CURRENT_MODEL_ID: 'SET_CURRENT_MODEL_ID',
  SET_CURRENT_MODEL_DETAIL: 'SET_CURRENT_MODEL_DETAIL',
  SET_LOADING: 'SET_LOADING',
  SET_VISIBLE: 'SET_VISIBLE',
}

export const dispatch = (action: Action) => {
  switch (action.type) {
    case types.SET_MODEL_LIST: {
      state.modelList.next(action.payload)
      break
    }
    case types.SET_CURRENT_MODEL_ID: {
      state.currentModelId.next(action.payload)
      break
    }
    case types.SET_CURRENT_MODEL_DETAIL: {
      state.currentModelDetail.next(action.payload)
      break
    }
    case types.SET_LOADING: {
      state.loading.next({ ...state.loading.value, ...action.payload })
      break
    }
    case types.SET_VISIBLE: {
      state.visible.next(action.payload)
      break
    }
    default:
  }
}

export const actions = {
  async loadModelList() {
    dispatch({ type: types.SET_LOADING, payload: { modelTable: true } })
    try {
      const dto: any = { statusList: [1] }
      const res = await ssp.getPageShift(1, dto, 999)
      if (!state.currentModelId.value || !res.records.find(item => item.id === state.currentModelId.value)) {
        dispatch({ type: types.SET_CURRENT_MODEL_ID, payload: res.records[0]?.id || '' })
      }
      dispatch({ type: types.SET_MODEL_LIST, payload: res.records })
      if (res.records?.length === 0) {
        state.currentModelId.next('')
        state.currentModelDetail.next(null)
      }
      dispatch({ type: types.SET_LOADING, payload: { modelTable: false } })
    } catch (e) {
      dispatch({ type: types.SET_LOADING, payload: { modelTable: false } })
    }
  },
  setCurrentModelId(id: string) {
    dispatch({ type: types.SET_CURRENT_MODEL_ID, payload: id })
  },
  setCurrentModelDetail(detail) {
    state.currentModelDetail.next(detail)
  },
  async loadModelDetail() {
    dispatch({ type: types.SET_LOADING, payload: { modelDetail: true } })
    try {
      const res = await ssp.getShiftDetail(state.currentModelId.value)
      res.shiftDetailVOList.sort((a, b) => {
        return a.shiftIndex > b.shiftIndex ? 1 : -1
      })
      res.shiftDetailVOList.forEach(item => {
        item.shiftRestTimeVOList.sort((a, b) => {
          return a.restIndex > b.restIndex ? 1 : -1
        })
      })
      dispatch({ type: types.SET_CURRENT_MODEL_DETAIL, payload: res })
      dispatch({ type: types.SET_LOADING, payload: { modelDetail: false } })
    } catch (e) {
      dispatch({ type: types.SET_LOADING, payload: { modelDetail: false } })
    }
  },
  setVisible(visible: Visible) {
    dispatch({ type: types.SET_VISIBLE, payload: visible })
  },
  async createModel(body: any) {
    const keys = Object.keys(body)
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'startTime' || keys[i] === 'endTime' || keys[i] === 'colour') continue
      if (!body[keys[i]]) {
        message.error(i18n.t('page_calendar_complete'))
        return Promise.reject()
      }
    }
    const temp = cloneDeep(body)
    temp.shiftDetailDTOList = temp.shiftDetailDTOList.slice(0, temp.number)
    temp.shiftDetailDTOList.forEach((item, index) => {
      temp.shiftDetailDTOList[index].shiftRestTimeDTOList = item.shiftRestTimeDTOList.slice(0, item.restNumber)
    })
    dispatch({ type: types.SET_LOADING, payload: { createForm: true } })
    try {
      await ssp.saveShift(temp)
      dispatch({ type: types.SET_LOADING, payload: { createForm: false } })
      message.success(i18n.t('page_resource_pool_success'))
      dispatch({ type: types.SET_VISIBLE, payload: { createForm: false } })
    } catch (e) {
      dispatch({ type: types.SET_LOADING, payload: { createForm: false } })
    }
  },
  async updateModel(body: any, isUsed: boolean) {
    const keys = Object.keys(body)
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'startTime' || keys[i] === 'endTime' || keys[i] === 'colour') continue
      if (!body[keys[i]]) {
        message.error(i18n.t('page_calendar_complete'))
        return Promise.reject()
      }
    }
    const temp = cloneDeep(body)
    temp.shiftDetailDTOList = temp.shiftDetailDTOList.slice(0, temp.number)
    temp.shiftDetailDTOList.forEach((item, index) => {
      temp.shiftDetailDTOList[index].shiftRestTimeDTOList = item.shiftRestTimeDTOList.slice(0, item.restNumber)
    })
    delete temp.code
    dispatch({ type: types.SET_LOADING, payload: { updateForm: true } })
    try {
      if (isUsed) {
        await ssp.updateShiftBasic({ colour: temp.colour, id: temp.id, name: temp.name } as ShiftUpdateBasicDTO)
      } else {
        await ssp.updateShiftBasic({ colour: temp.colour, id: temp.id, name: temp.name } as ShiftUpdateBasicDTO)
        delete temp.code
        delete temp.name
        delete temp.colour
        await ssp.updateShift(temp)
      }

      dispatch({ type: types.SET_LOADING, payload: { updateForm: false } })
      message.success(i18n.t('page_resource_pool_success'))
      dispatch({ type: types.SET_VISIBLE, payload: { updateForm: false } })
    } catch (e) {
      dispatch({ type: types.SET_LOADING, payload: { updateForm: false } })
    }
  },
}
